import { Button, Label } from '@/components/ui'
import type { ComboRow, ComboRowIngredient, MenuItem } from '@/types/menu'
import type { SelectedModifier, IngredientVariationSelection } from '@/types/cart'
import type { RuleEvaluationResult } from '@/types/logicRules'
import { isRowVisible, isIngredientEnabled, getSelectionLimit } from '@/utils/ruleEngine'
import { rowHasDynamicPricing } from '@/utils/ingredientPricing'
import { formatPrice } from '@/utils/formatting'
import { QuantityRow } from './QuantityRow'

interface ComboMainMealRowProps {
  row: ComboRow
  customizations: SelectedModifier[]
  onCustomizationChange: (customizations: SelectedModifier[]) => void
  ingredientVariationSelections: IngredientVariationSelection[]
  ruleResult: RuleEvaluationResult
  getIngredientsToDisplay: (row: ComboRow) => ComboRowIngredient[]
  menuItem: MenuItem
  onVariationSelect: (
    row: ComboRow,
    ing: ComboRowIngredient,
    variationId: string,
    variationName: string,
    priceAdjustment: number
  ) => void
  onIngredientSelect: (
    row: ComboRow,
    ing: ComboRowIngredient,
    newCustomizations: SelectedModifier[]
  ) => void
  getIngredientDisplayPrice: (
    row: ComboRow,
    ing: ComboRowIngredient
  ) => { label: string; value: number } | null
}

export function ComboMainMealRow({
  row,
  customizations,
  onCustomizationChange,
  ingredientVariationSelections,
  ruleResult,
  getIngredientsToDisplay,
  menuItem,
  onVariationSelect,
  onIngredientSelect,
  getIngredientDisplayPrice,
}: ComboMainMealRowProps) {
  if (!isRowVisible(row.id, ruleResult, true)) return null

  const displayIngredients = getIngredientsToDisplay(row)
  const selectedIngredients = customizations.filter((m) => m.groupId === row.id)
  const selectedCount = selectedIngredients.length
  const maxSelections = getSelectionLimit(row.id, ruleResult, row.maxSelections)
  const maxReached = selectedCount >= maxSelections
  const isDynamic = rowHasDynamicPricing(menuItem, row.id)
  const freeQuantity = menuItem.rowPricingRules?.[row.id]?.freeQuantity ?? 0
  const pricingLabel = menuItem.rowPricingRules?.[row.id]?.label

  if (row.selectionType === 'quantity') {
    const qtyMap: Record<string, number> = {}
    for (const mod of selectedIngredients) {
      qtyMap[mod.optionId] = mod.quantity ?? 1
    }

    const qtyIngredients = displayIngredients.map((ing) => {
      const dynamicPrice = getIngredientDisplayPrice(row, ing)
      return {
        id: ing.id,
        name: ing.ingredient.name,
        price: dynamicPrice ? dynamicPrice.value : ing.additionalPrice,
        isEnabled: isIngredientEnabled(row.id, ing.id, ruleResult, true),
      }
    })

    const handleQtyChange = (ingredientId: string, newQty: number) => {
      const ing = displayIngredients.find((i) => i.id === ingredientId)
      if (!ing) return
      const storedPrice = isDynamic ? 0 : ing.additionalPrice
      let updated = customizations.filter(
        (m) => !(m.groupId === row.id && m.optionId === ingredientId)
      )
      if (newQty > 0) {
        updated = [
          ...updated,
          {
            groupId: row.id,
            groupName: row.title,
            optionId: ingredientId,
            optionName: ing.ingredient.name,
            priceAdjustment: storedPrice,
            quantity: newQty,
          },
        ]
      }
      onCustomizationChange(updated)
    }

    return (
      <QuantityRow
        key={row.id}
        rowTitle={row.title}
        minSelections={row.minSelections}
        maxSelections={maxSelections}
        maxQuantityPerItem={row.maxQuantityPerItem ?? null}
        ingredients={qtyIngredients}
        quantities={qtyMap}
        onQuantityChange={handleQtyChange}
        freeQuantity={freeQuantity}
        isDynamic={isDynamic}
        pricingLabel={pricingLabel}
      />
    )
  }

  return (
    <div key={row.id} className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Label className="text-sm font-medium text-muted-foreground">
            {row.title}
            {row.minSelections > 0 && <span className="text-destructive ml-1">*</span>}
          </Label>
          {isDynamic && freeQuantity > 0 && (
            <span className="text-[11px] text-primary font-medium">
              ({Math.min(freeQuantity, selectedCount)} of {freeQuantity} free)
            </span>
          )}
          {pricingLabel && (
            <span className="text-[11px] text-muted-foreground font-medium">
              · {pricingLabel}
            </span>
          )}
          {row.minSelections > 0 && selectedCount < row.minSelections && (
            <span className="text-[11px] text-destructive font-medium">
              Select at least {row.minSelections}
            </span>
          )}
        </div>
        <span className="text-xs text-muted-foreground">
          ({selectedCount}/{maxSelections})
        </span>
      </div>
      <div className="flex flex-wrap gap-2">
        {displayIngredients.map((ing) => {
          const isSelected = selectedIngredients.some((m) => m.optionId === ing.id)
          const isDisabledByRule = !isIngredientEnabled(row.id, ing.id, ruleResult, true)
          const isDisabled = isDisabledByRule || (!isSelected && maxReached)
          const dynamicPrice = getIngredientDisplayPrice(row, ing)
          const storedPrice = isDynamic ? 0 : ing.additionalPrice

          return (
            <Button
              key={ing.id}
              variant={isSelected ? 'black' : 'outline'}
              className={`px-3 py-1.5 h-auto rounded-full text-xs ${
                isDisabled && !isSelected ? 'bg-muted text-muted-foreground border-border' : ''
              }`}
              disabled={isDisabled}
              onClick={() => {
                if (isDisabled) return
                if (isSelected) {
                  onCustomizationChange(
                    customizations.filter((m) => !(m.groupId === row.id && m.optionId === ing.id))
                  )
                } else if (!maxReached) {
                  onIngredientSelect(row, ing, [
                    ...customizations,
                    {
                      groupId: row.id,
                      groupName: row.title,
                      optionId: ing.id,
                      optionName: ing.ingredient.name,
                      priceAdjustment: storedPrice,
                    },
                  ])
                }
              }}
            >
              {ing.ingredient.name}
              {dynamicPrice ? (
                dynamicPrice.value !== 0 ? (
                  <span className="ml-1">{dynamicPrice.label}</span>
                ) : (
                  <span className="ml-1 text-primary">Included</span>
                )
              ) : (
                ing.additionalPrice > 0 && (
                  <span className="ml-1">+{formatPrice(ing.additionalPrice, menuItem.currency, false)}</span>
                )
              )}
            </Button>
          )
        })}
      </div>
      {displayIngredients
        .filter(
          (ing) =>
            ing.ingredient.hasVariations &&
            ing.ingredient.variationGroup &&
            selectedIngredients.some((m) => m.optionId === ing.id)
        )
        .map((ing) => {
          const vg = ing.ingredient.variationGroup!
          const currentSelection = ingredientVariationSelections.find(
            (vs) => vs.rowId === row.id && vs.rowIngredientId === ing.id
          )
          return (
            <div key={`var-${ing.id}`} className="ml-4 pl-3 border-l-2 border-border">
              <span className="text-xs text-muted-foreground mb-1 block">
                {ing.ingredient.name} — {vg.name}
              </span>
              <div className="flex flex-wrap gap-1.5">
                {vg.variations
                  .slice()
                  .sort((a, b) => a.sort_order - b.sort_order)
                  .map((v) => {
                    const isVarSelected = currentSelection?.variationId === v.id
                    return (
                      <Button
                        key={v.id}
                        variant={isVarSelected ? 'default' : 'outline'}
                        className={`px-2.5 py-1 h-auto rounded-full text-[11px] ${
                          isVarSelected ? 'bg-primary text-primary-foreground border-primary' : ''
                        }`}
                        onClick={() => onVariationSelect(row, ing, v.id, v.name, v.price ?? 0)}
                      >
                        {v.name}
                        {v.price !== undefined && v.price > 0 && (
                          <span className="ml-0.5">{formatPrice(v.price, menuItem.currency, false)}</span>
                        )}
                      </Button>
                    )
                  })}
              </div>
            </div>
          )
        })}
    </div>
  )
}
